import React, { useEffect, useMemo, useState } from "react";
import PageTitle from "../../ui/PageTitle";
import { Accordion, Badge, Button, Col, Container, Form, Row, Spinner, Stack } from "react-bootstrap";
import { Goal } from "../../../types/goal";
import { fetchGoalCategories, fetchGoalTemplates, postGoalCategory } from "../../../services/httpClient";
import useApp from "../../../utils/useApp";
import GoalTemplateCard from "./GoalTemplateCard";
import GoalCategoryCard from "./GoalCategoryCard";
import SearchBar from "../../ui/SearchBar";
import CreateGoalCard from "../../ui/CreateGoalCard";
import SaveChangesModal from "../../ui/SaveChangesModal";
import Input from "../../ui/Input";

interface OrgTemplateState {
  searchRef: any;
  setOrgToSave: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const OrgGoalTemplates = ({ searchRef, setOrgToSave, setShowToast, setToastText, setToastBody }: OrgTemplateState) => {
  const { auth, showMenu }: any = useApp();
  const [showCreateGoal, setShowCreateGoal] = useState<boolean>(false);
  const [goalSaved, setGoalSaved] = useState<boolean>(false);
  const [goalTemplates, setGoalTemplates] = useState<Goal[]>([]);
  const [goalTemplatesFiltered, setGoalTemplatesFiltered] = useState<Goal[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showCategoryView, setShowCategoryView] = useState<boolean>(false);
  const [showAddCategory, setShowAddCategory] = useState<boolean>(false);
  const [newCategoryName, setNewCategoryName] = useState<string>("");
  const [goalCategories, setGoalCategories] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    getGoalTemplates(true);
    getGoalCategories();
  }, [auth?.profile]);

  const getGoalTemplates = (loading: boolean) => {
    setIsLoading(loading);

    setErrMsg("");

    fetchGoalTemplates(auth?.profile?.userOrgID)
      .then((res: any) => {
        var temps = res[0].data;
        temps = temps.map((t: any) => {
          return { ...t, key: t.id };
        });
        setGoalTemplates(temps);
        setGoalTemplatesFiltered(temps);
        setIsLoading(false);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        setIsLoading(false);
      });
  };

  const getGoalCategories = () => {
    fetchGoalCategories()
      .then((res: any) => {
        let cats = res[0].data;
        cats = [{ id: 0, key: "0", name: "None" }, ...cats];
        setGoalCategories(cats);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
      });
  };

  const onClickAddGoalCategory = (newCategoryName: string) => {
    const newCategory = newCategoryName.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

    return postGoalCategory({ name: newCategory })
      .then((res: any) => {
        setShowToast(true);
        setToastText("Category Created");
        setToastBody(newCategory);
        setNewCategoryName("");
        setShowAddCategory(false);
        getGoalCategories();
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        throw err;
      });
  };

  const onClickSaveSubmitNewCategory = (e: any) => {
    e?.preventDefault();
    onClickAddGoalCategory(newCategoryName);
  };

  const onClickAddCategoryOnly = () => {
    setShowAddCategory(true);
    setErrMsg("");
  };

  const onClickAddCategoryOnlyCancel = () => {
    setNewCategoryName("");
    setShowAddCategory(false);
    setErrMsg("");
  };

  const onUpdateNewCategoryName = (e: any) => {
    setNewCategoryName(e.target.value);
    setErrMsg("");
  };

  const onClickViewCategories = () => {
    setShowCategoryView(true);
    setErrMsg("");
  };

  const onClickBackFromCategories = () => {
    setShowCategoryView(false);
    setShowAddCategory(false);
    setErrMsg("");
  };

  const onClickCreateGoal = () => {
    //Set Create Goal Card to null values
    setGoalSaved(false);
    setShowCreateGoal(true);
  };

  const onClickCreateGoalDone = () => {
    setShowCreateGoal(false);
    getGoalTemplates(true);
  };

  const onPerformSearchLogic = (text: string) => {
    const lowerText = text.toLowerCase();
    var myFiltered: any;
    var myCases = goalTemplates;

    //filter by name of case
    myFiltered = myCases?.filter((item: any) => {
      return item.name.toLowerCase().match(lowerText);
    });

    setGoalTemplatesFiltered(myFiltered);
  };

  const groupedGoalTemplates = useMemo(() => {
    const groups = new Map<string, { key: string; name: string; goals: Goal[] }>();

    goalTemplatesFiltered.forEach((g: Goal) => {
      const key = g.category_id ? String(g.category_id) : "uncategorized";
      const name = g.category_name || "Uncategorized";

      if (!groups.has(key)) {
        groups.set(key, { key, name, goals: [] });
      }
      groups.get(key)!.goals.push(g);
    });

    const sorted = Array.from(groups.values()).sort((a, b) => {
      if (a.key === "uncategorized") return 1;
      if (b.key === "uncategorized") return -1;
      return a.name.localeCompare(b.name);
    });

    sorted.forEach((group) => group.goals.sort((a, b) => a.name.localeCompare(b.name)));

    return sorted;
  }, [goalTemplatesFiltered]);

  return (
    <>
      {isLoading ? (
        <Container className={showMenu ? "lg div-main-menu" : "lg div-main"}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : showCategoryView ? (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <PageTitle
            title="Goal Categories"
            type="section"
            buttonTitle="Add Category"
            onButtonClick={onClickAddCategoryOnly}
            buttonDisabled={showAddCategory}
            backButtonText="Back to Goal Templates"
            onBackClick={onClickBackFromCategories}
          />
          <Row className="mb-3"></Row>
          {showAddCategory && (
            <Form onSubmit={(e: any) => onClickSaveSubmitNewCategory(e)}>
              <Row className="mb-3">
                <Col xs={6}>
                  <Input
                    label="Name"
                    type="text"
                    value={newCategoryName}
                    onUpdate={(e: any) => onUpdateNewCategoryName(e)}
                    hasFocus={true}
                  />
                </Col>
                <Col xs={"auto"} className="d-flex align-items-center">
                  <Button type="submit" className="app-secondary-color button me-3" disabled={!newCategoryName}>
                    Add
                  </Button>
                  <div className="vr my-2" />
                  <Button className="app-primary-color button ms-3" onClick={onClickAddCategoryOnlyCancel}>
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
          <Row>
            <Col xs={6}>
              {goalCategories?.length === 0 ? (
                <p className="mt-2">No Categories</p>
              ) : (
                goalCategories
                  .slice(1)
                  .map((category: any) => (
                    <GoalCategoryCard
                      key={category.id}
                      category={category}
                      getGoalCategories={getGoalCategories}
                      setOrgToSave={setOrgToSave}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      setErrMsg={setErrMsg}
                    />
                  ))
              )}
            </Col>
          </Row>
        </>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          {showCreateGoal ? (
            <PageTitle title="Create Goal Template" type="section" backButtonText="Back to Goal Templates" onBackClick={onClickCreateGoalDone} />
          ) : (
            <Row>
              <Col xs={6}>
                <h4 className="app-header-text">Goal Templates</h4>
              </Col>
              <Col className="text-end">
                <Button className="app-secondary-color button me-3" onClick={onClickViewCategories}>
                  Categories
                </Button>
                <Button className="app-primary-color button" onClick={onClickCreateGoal}>
                  Create Template
                </Button>
              </Col>
            </Row>
          )}
          <Row className="mb-3"></Row>
          {showCreateGoal && (
            <CreateGoalCard
              goalSaved={goalSaved}
              setGoalSaved={setGoalSaved}
              templateMode={true}
              setShowToast={setShowToast}
              setToastText={setToastText}
              setToastBody={setToastBody}
            />
          )}
          {!showCreateGoal && (
            <>
              <Row className="my-3">
                <Col xs={12} className="d-flex justify-content-center">
                  <SearchBar
                    cssClass="search w-100"
                    placeholder="Search Goal Templates"
                    searchText={searchText}
                    setSearchText={setSearchText}
                    searchLogic={onPerformSearchLogic}
                    externalRef={searchRef}
                  />
                </Col>
              </Row>
              <Row className="mt-3">
                <Col xs={12}>
                  {goalTemplatesFiltered?.length === 0 ? (
                    <p className="mt-2">No Current Templates</p>
                  ) : (
                    <Accordion
                      alwaysOpen
                      key={groupedGoalTemplates.map((group) => group.key).join(",")}
                      defaultActiveKey={groupedGoalTemplates.map((group) => group.key)}
                    >
                      {groupedGoalTemplates.map((group) => (
                        <Accordion.Item eventKey={group.key} key={group.key} className="border-0">
                          <Accordion.Header className="accordion-head">
                            <h5 className="m-0">{group.name}</h5>
                            <Badge bg="none" className="app-secondary-color ms-2">
                              {group.goals.length}
                            </Badge>
                          </Accordion.Header>
                          <Accordion.Body>
                            <Stack gap={3}>
                              {group.goals.map((g: Goal) => (
                                <GoalTemplateCard
                                  key={g.id}
                                  goal={g}
                                  getGoalTemplates={getGoalTemplates}
                                  getGoalCategories={getGoalCategories}
                                  setSearchText={setSearchText}
                                  categories={goalCategories}
                                  setOrgToSave={setOrgToSave}
                                  setShowToast={setShowToast}
                                  setToastText={setToastText}
                                  setToastBody={setToastBody}
                                  setErrMsg={setErrMsg}
                                />
                              ))}
                            </Stack>
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  )}
                </Col>
              </Row>
              <SaveChangesModal showModal={showModal} setShowModal={setShowModal} path="/settings" />
            </>
          )}
        </>
      )}
    </>
  );
};

export default React.memo(OrgGoalTemplates);
